import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Wand2, Wrench, Sparkles, Beaker } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import AILabShell from "@/components/ai-lab/AILabShell";
import CategoryIcon from "@/components/ai-lab/CategoryIcon";
import BrandProfileBanner from "@/components/ai-lab/BrandProfileBanner";
import TaskCard from "@/components/ai-lab/TaskCard";
import { CATEGORIES, ACCENT_CLASSES } from "@/data/ai-lab/categories";
import { PROMPT_TASKS, TASK_BY_ID, FEATURED_TASK_IDS, tasksForCategory } from "@/data/ai-lab/promptLibrary";
import { AI_TOOLS } from "@/data/ai-lab/aiTools";
import { useMenuVisibility } from "@/context/MenuVisibilityContext";

/**
 * AILabDashboard — route: /ai-cpg-tools
 * Hero + Brand Profile banner + big tiles (Prompt Builder, 12 category pages, Tools directory) + featured prompts.
 */
export default function AILabDashboard() {
  const featured = FEATURED_TASK_IDS.map((id) => TASK_BY_ID[id]).filter(Boolean);
  // Admin Menu Directory → Pages → AI CPG Tools → Formulation Creator
  // can hide this entire spotlight section without a redeploy.
  const { isPageElementHidden } = useMenuVisibility();
  const formulationHidden = isPageElementHidden("ai-cpg-tools-formulation");

  return (
    <AILabShell>
      {/* Hero — same visual language as the Welcome page IntroSection
          (dark shell shell-side gradient, pink small-caps eyebrow,
          gradient-band top accent, brand-pink pill CTA). Uses `on-dark`
          so global h1 typography flips to white on this dark surface. */}
      <section
        className="on-dark mb-6 relative overflow-hidden rounded-3xl bg-gradient-to-br from-shell-side to-shell-side/95 p-6 text-white sm:p-10"
        data-surface="dark"
        data-testid="ai-cpg-tools-hero"
      >
        <div className="absolute inset-x-0 top-0 h-1 grad-band" />
        <div className="max-w-2xl">
          <p className="text-[10px] uppercase tracking-[0.28em] text-shell-accent font-bold inline-flex items-center gap-1.5">
            <Sparkles className="w-3 h-3" /> AI CPG Tools
          </p>
          <h1 className="mt-3 font-display text-3xl md:text-5xl leading-[1.05] tracking-tight">
            AI tools built specifically to help you launch and grow your CPG brand.
          </h1>
          <p className="mt-4 max-w-lg text-[15px] leading-relaxed text-white/85">
            Pick what you want to create, answer a few simple questions, and get an expert-level prompt you can paste into ChatGPT, Claude, Midjourney, Runway or any other AI tool — with your brand already filled in.
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Link
              to="/ai-cpg-tools/prompt-builder"
              className="inline-flex items-center gap-1.5 rounded-full bg-primary text-primary-foreground px-5 py-2.5 text-sm font-bold hover:bg-primary/90 active:bg-primary/95 transition"
            >
              <Wand2 className="w-4 h-4" /> Open Prompt Builder
            </Link>
            <Link
              to="/ai-cpg-tools/tools"
              className="inline-flex items-center gap-1.5 rounded-full border border-white/25 bg-transparent text-white px-5 py-2.5 text-sm font-semibold hover:bg-white/10 transition"
            >
              <Wrench className="w-4 h-4" /> Recommended tools
            </Link>
          </div>
          <p className="mt-4 text-xs text-white/60">
            {PROMPT_TASKS.length} specialized CPG prompts · {CATEGORIES.length} toolkits · {AI_TOOLS.length} vetted tools
          </p>
        </div>
      </section>

      <div className="mb-8" data-testid="ai-cpg-tools-brand-profile-banner"><BrandProfileBanner /></div>

      {/* Tiles */}
      <section className="mb-10" data-testid="ai-cpg-tools-categories-section">
        <h2 className="mb-3 text-lg font-semibold">What do you want to create?</h2>
        <div className="card-grid-auto gap-3">
          <Link to="/ai-cpg-tools/prompt-builder" data-testid="ai-cpg-tools-prompt-builder-tile" className="group col-span-2 rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:col-span-3 lg:col-span-2">
            <Card className="h-full border-violet-300 bg-gradient-to-br from-violet-600 to-fuchsia-600 text-white transition-transform group-hover:-translate-y-0.5 dark:border-violet-700">
              <CardContent className="flex h-full flex-col justify-between gap-4 p-5">
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-white/15"><Wand2 className="h-6 w-6" /></span>
                <div>
                  <h3 className="text-xl font-bold">Prompt Builder</h3>
                  <p className="mt-1 text-sm text-violet-100">The centerpiece. Choose a task, answer simple questions, get a polished prompt for any AI tool.</p>
                </div>
                <span className="inline-flex items-center gap-1 text-sm font-medium">Start building <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" /></span>
              </CardContent>
            </Card>
          </Link>

          {CATEGORIES.map((cat) => {
            const count = tasksForCategory(cat.id).length;
            return (
              <Link key={cat.id} to={`/ai-cpg-tools/${cat.slug}`} className="group rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                <Card className={cn("h-full border transition-transform group-hover:-translate-y-0.5", ACCENT_CLASSES[cat.accent]?.tile)}>
                  <CardContent className="flex h-full flex-col gap-3 p-4">
                    <CategoryIcon icon={cat.icon} accent={cat.accent} />
                    <div>
                      <h3 className="font-semibold leading-tight text-foreground">{cat.name}</h3>
                      <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{cat.tagline}</p>
                    </div>
                    <span className="mt-auto text-[11px] font-medium uppercase tracking-wide opacity-80">{count} prompts</span>
                  </CardContent>
                </Card>
              </Link>
            );
          })}

          <Link to="/ai-cpg-tools/tools" data-testid="ai-cpg-tools-tools-directory-tile" className="group rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            <Card className="h-full border-dashed transition-transform group-hover:-translate-y-0.5">
              <CardContent className="flex h-full flex-col gap-3 p-4">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-slate-800 text-white"><Wrench className="h-5 w-5" /></span>
                <div>
                  <h3 className="font-semibold leading-tight">AI Tools Directory</h3>
                  <p className="mt-1 text-xs text-muted-foreground">Every tool we recommend, organized by job — with one clear pick per task.</p>
                </div>
                <span className="mt-auto text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{AI_TOOLS.length} tools</span>
              </CardContent>
            </Card>
          </Link>
        </div>
      </section>

      {/* Formulation Creator — its own full-width section, promoted above the featured prompts.
          Hidden entirely when Admin → Menu Directory → Pages → AI CPG Tools flips it off. */}
      {!formulationHidden && (
      <section className="mb-10" data-testid="ai-cpg-tools-formulation-section">
        <div className="overflow-hidden rounded-2xl border bg-gradient-to-br from-violet-950 via-fuchsia-900 to-slate-950 text-white shadow-xl">
          <div className="grid gap-8 p-6 sm:p-10 lg:grid-cols-[1.5fr_1fr] lg:items-center">
            <div>
              <p className="mb-3 inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-medium uppercase tracking-wider">
                <Beaker className="h-3.5 w-3.5" /> Formulation
              </p>
              <h2 className="text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">Formulation Creator</h2>
              <p className="mt-3 max-w-2xl text-sm text-slate-200 sm:text-base">
                Draft a co-manufacturer-ready creative brief for any CPG product — beverages, food, supplements, personal care, pet, household. Warren generates the ingredient deck, macro targets, claim alignment, benchmark competitors, and regulatory flags in about 20 seconds.
              </p>
              <ul className="mt-5 grid gap-2 text-sm text-slate-200 sm:grid-cols-2">
                <li className="flex items-start gap-2"><span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-fuchsia-300" /> Ingredient deck with rationale</li>
                <li className="flex items-start gap-2"><span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-fuchsia-300" /> Macro targets &amp; claim alignment</li>
                <li className="flex items-start gap-2"><span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-fuchsia-300" /> Benchmark competitor scan</li>
                <li className="flex items-start gap-2"><span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-fuchsia-300" /> Regulatory flags surfaced</li>
              </ul>
              <div className="mt-6 flex flex-wrap gap-2">
                <Button asChild size="lg" className="bg-white text-slate-900 hover:bg-slate-200" data-testid="ai-cpg-tools-formulation-open-cta">
                  <Link to="/ai-cpg-tools/formulation-creator"><Beaker className="mr-2 h-4 w-4" /> Open Formulation Creator</Link>
                </Button>
                <Button asChild size="lg" variant="outline" className="border-white/30 bg-transparent text-white hover:bg-white/10 hover:text-white">
                  <Link to="/ai-cpg-tools/brand-profile">Set brand context first <ArrowRight className="ml-2 h-4 w-4" /></Link>
                </Button>
              </div>
              <p className="mt-4 text-xs text-slate-400">Uses your Brand Profile · Metered per brief · Copy-paste ready</p>
            </div>
            <div className="hidden lg:flex items-center justify-center">
              <div className="relative">
                <div className="absolute inset-0 -m-6 rounded-full bg-fuchsia-500/20 blur-2xl" aria-hidden="true" />
                <div className="relative rounded-2xl border border-white/10 bg-white/5 p-8 backdrop-blur">
                  <Beaker className="h-28 w-28 text-fuchsia-200" strokeWidth={1.25} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* Featured prompts */}
      <section data-testid="ai-cpg-tools-popular-section">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-lg font-semibold">Popular prompts</h2>
          <Button asChild variant="ghost" size="sm"><Link to="/ai-cpg-tools/prompt-builder">See all {PROMPT_TASKS.length} <ArrowRight className="ml-1 h-4 w-4" /></Link></Button>
        </div>
        <div className="card-grid-auto gap-3">
          {featured.map((task) => <TaskCard key={task.id} task={task} showCategory />)}
        </div>
      </section>
    </AILabShell>
  );
}
